import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { ClipboardCheck, X, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const POLL_MS = 5 * 60 * 1000;

function todayKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const PendingReviewBanner = () => {
  const { t } = useTranslation();
  const { user, isClient, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [summary, setSummary] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [bannerHidden, setBannerHidden] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get('/reports/my-review/pending-summary');
      setSummary(res.data);
    } catch {
      setSummary(null);
    }
  }, []);

  useEffect(() => {
    if (!user || isClient || isImpersonating) {
      setSummary(null);
      return undefined;
    }
    fetchSummary();
    const interval = setInterval(fetchSummary, POLL_MS);
    const onFocus = () => fetchSummary();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, isClient, isImpersonating, fetchSummary]);

  // Daily 8am modal — show once per local day when there's pending work
  useEffect(() => {
    if (!user) return;
    if (!summary || summary.pending_reports <= 0) return;
    const hour = new Date().getHours();
    if (hour < 8) return;
    const storageKey = `pendingReviewModalShown:${user.id}`;
    if (localStorage.getItem(storageKey) === todayKey()) return;
    setShowModal(true);
    localStorage.setItem(storageKey, todayKey());
  }, [summary, user]);

  // Reset dismiss when summary changes (new pending work appears)
  useEffect(() => {
    setBannerHidden(false);
  }, [summary?.pending_reports, summary?.pending_entries]);

  if (!user || isClient || isImpersonating) return null;
  if (!summary || summary.pending_reports <= 0) return null;
  if (location.pathname.startsWith('/report-review')) return null;

  const goReview = () => {
    setShowModal(false);
    setBannerHidden(false);
    navigate('/report-review');
  };

  const daysWaiting = summary.oldest_notified_at
    ? Math.max(0, Math.floor((Date.now() - new Date(summary.oldest_notified_at).getTime()) / 86400000))
    : 0;

  // If every pending report has all entries reviewed, the only thing left is to click Confirm.
  const onlyConfirmPending =
    summary.pending_entries === 0 && summary.awaiting_confirm_reports >= summary.pending_reports;

  const bannerSummary = onlyConfirmPending
    ? t('reviewReminder.bannerConfirm', { count: summary.pending_reports })
    : t('reviewReminder.bannerCount', {
        count: summary.pending_entries,
        reports: summary.pending_reports,
      });

  const modalBody = onlyConfirmPending
    ? t('reviewReminder.modalBodyConfirm', { count: summary.pending_reports })
    : t('reviewReminder.modalBody', {
        count: summary.pending_entries,
        reports: summary.pending_reports,
      });

  return (
    <>
      {!bannerHidden && (
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 9997,
            background: 'linear-gradient(90deg, #dc2626, #b91c1c)',
            color: '#fff',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            fontSize: 13,
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            flexWrap: 'wrap',
          }}
        >
          <AlertTriangle size={16} />
          <span>
            <strong>
              {onlyConfirmPending
                ? t('reviewReminder.bannerTitleConfirm')
                : t('reviewReminder.bannerTitle')}
            </strong>
            {' — '}
            {bannerSummary}
          </span>
          <button
            onClick={goReview}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.4)',
              color: '#fff',
              borderRadius: 4,
              padding: '4px 12px',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <ClipboardCheck size={13} />
            {t('reviewReminder.bannerCta')}
          </button>
          <button
            onClick={() => setBannerHidden(true)}
            title={t('reviewReminder.bannerDismiss')}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.85)',
              cursor: 'pointer',
              padding: 4,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10001,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            style={{
              background: 'var(--c-surface)',
              color: 'var(--c-text)',
              border: '1px solid var(--c-border)',
              borderRadius: 10,
              maxWidth: 480,
              width: '100%',
              padding: 24,
              boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <AlertTriangle size={22} color="#dc2626" />
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                {onlyConfirmPending
                  ? t('reviewReminder.modalTitleConfirm')
                  : t('reviewReminder.modalTitle')}
              </h2>
            </div>
            <p style={{ marginTop: 0, marginBottom: 8, lineHeight: 1.5 }}>
              {modalBody}
            </p>
            {daysWaiting > 0 && (
              <p style={{ marginTop: 0, marginBottom: 16, lineHeight: 1.5, color: 'var(--c-text-muted)', fontSize: 13 }}>
                {t('reviewReminder.modalBodyOldest', { days: daysWaiting })}
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                className="btn btn-ghost"
                onClick={() => setShowModal(false)}
              >
                {t('reviewReminder.modalLater')}
              </button>
              <button
                className="btn btn-primary"
                onClick={goReview}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <ClipboardCheck size={14} />
                {t('reviewReminder.modalCta')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PendingReviewBanner;
